import React, { useEffect, useState } from 'react';
import { Card, Select, Spin } from 'antd';
import { useQuery } from '@apollo/client';
import { queries } from '../../graphql/graphql';
import { DirectoriesValuesByIdsResponse, DirectoryResponse } from '../../types/directory';

interface ContractorAvailableArea {
  states: string[];
  counties: string[];
}

interface ContractorAvailableAreaSelectorProps {
  value?: ContractorAvailableArea;
  onChange?: (value: ContractorAvailableArea) => void;
  disabled?: boolean;
}

const defaultValue: ContractorAvailableArea = {
  states: [],
  counties: [],
};

export const ContractorAvailableAreaSelector: React.FC<ContractorAvailableAreaSelectorProps> = ({
  value = defaultValue,
  onChange,
  disabled = false,
}) => {
  const [selectedStates, setSelectedStates] = useState<string[]>(value.states || []);
  const [selectedCounties, setSelectedCounties] = useState<string[]>(value.counties || []);
  const [countyParentMap, setCountyParentMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setSelectedStates(value.states || []);
    setSelectedCounties(value.counties || []);
  }, [value]);

  const { loading: statesLoading, data: statesData } = useQuery<DirectoryResponse>(
    queries.directories.DIRECTORY,
    {
      variables: { slug: 'us-states' },
    }
  );

  const { loading: countiesLoading, data: countiesData } = useQuery<DirectoriesValuesByIdsResponse>(
    queries.directories.DIRECTORIES_VALUES_BY_IDS,
    {
      variables: { ids: selectedStates },
      skip: selectedStates.length === 0,
    }
  );

  const getStateOptions = () => {
    if (!statesData?.directory) {
      return [];
    }

    return statesData.directory.values.map(option => ({
      label: option.name,
      value: option.directory || option.id,
    }));
  };

  const getCountyOptions = () => {
    if (!countiesData?.directoriesValuesByIds) {
      return [];
    }

    return countiesData.directoriesValuesByIds.map(option => ({
      label: option.name,
      value: option.directory || option.id,
      parentId: option.parentId,
    }));
  };

  const handleStatesChange = (states: string[]) => {
    const removedStates = selectedStates.filter(state => !states.includes(state));
    setSelectedStates(states);

    let updatedCounties = selectedCounties;
    if (removedStates.length) {
      updatedCounties = selectedCounties.filter(countyId => {
        const parentState = countyParentMap[countyId];
        return !parentState || !removedStates.includes(parentState);
      });
      if (updatedCounties.length !== selectedCounties.length) {
        setSelectedCounties(updatedCounties);
      }
    }

    onChange?.({ states, counties: updatedCounties });
  };

  const handleCountiesChange = (counties: string[]) => {
    setSelectedCounties(counties);
    onChange?.({ states: selectedStates, counties });
  };

  useEffect(() => {
    if (countiesData?.directoriesValuesByIds) {
      const map: Record<string, string> = {};
      countiesData.directoriesValuesByIds.forEach(item => {
        const valueId = item.directory || item.id;
        if (valueId) {
          map[valueId] = item.parentId || '';
        }
      });
      setCountyParentMap(map);
    }
  }, [countiesData]);

  return (
    <Card title="Available Area">
      <Select
        disabled={disabled || statesLoading}
        mode="multiple"
        style={{ width: '100%', marginBottom: 16 }}
        placeholder="Available States"
        value={selectedStates}
        onChange={handleStatesChange}
        options={getStateOptions()}
      />
      {selectedStates.length > 0 && (
        <>
          {countiesLoading ? (
            <Spin />
          ) : (
            <Select
              disabled={disabled}
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Available Counties"
              value={selectedCounties}
              onChange={handleCountiesChange}
              options={getCountyOptions()}
            />
          )}
        </>
      )}
    </Card>
  );
};
